import { useApiData } from "../../contexts/ApiData";
import { useTranslations } from "../../contexts/Translation";
import { useState } from "react";
import { Comment, ValidationError, ValidationResult } from "../../types";
import Input from "./Input";
import ErrorText from "./ErrorText";
import Button from "../Button/Button";
import AiCoach from "../AiCoach/AiCoach";
import styles from "./FormStyles.module.css";
import { MAX_COMMENT_CONTENT } from "../../lib/textLimits";

type CommentFormData = Comment & {};

type ComponentProps = {
  surveyId?: string;
  goalId?: string;
  orgGoalId?: string;
  addComment: (c: Comment) => void;
  enableAiComment?: boolean;
};

const CommentForm = ({ surveyId, goalId, orgGoalId, addComment, enableAiComment }: ComponentProps) => {
  const t = useTranslations("comments");
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const { post } = useApiData();
  const initialFormData = {
    id: crypto.randomUUID(),
    goalId: goalId || null,
    surveyId: surveyId || null,
    content: "",
  };
  const [formData, setFormData] = useState<CommentFormData>(initialFormData);

  const validateData = ({ content }: CommentFormData): ValidationResult => {
    const errors: ValidationError[] = [];
    if (!content) {
      errors.push({ name: "content", message: t("fieldRequired") });
    }

    return { errors: errors.length > 0 ? errors : undefined, success: errors.length === 0 };
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrors(errors.filter((err) => err.name !== e.target.name));
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationResult = validateData(formData);

    if (validationResult.success) {
      const url = orgGoalId ? `/comment/orggoal/${orgGoalId}` : goalId ? `/comment/goal/${goalId}` : `/comment/survey/${surveyId}`;

      const result = await post(url, formData);
      if (!result) {
        setErrors([{ name: "form", message: t("serverError") }]);
      } else {
        addComment(result);
        setFormData({ ...initialFormData, id: crypto.randomUUID() });
      }
    } else {
      setErrors(validationResult.errors || []);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {errors.filter((e) => e.name !== "form").length > 0 && <ErrorText variant='form' text={t("formErrors")} />}
      <ErrorText variant='form' error={errors.find((e) => e.name === "form")} />
      <Input
        error={errors.find((e) => e.name === "content")}
        type='textarea'
        name='content'
        maxLength={MAX_COMMENT_CONTENT}
        handleChange={handleChange}
        label={t("addCommentHeading")}
        value={formData.content}
      />
      <div className={styles.buttonContainer}>
        <Button>{t("submit")}</Button>
        {enableAiComment && (
          <AiCoach
            coachingType={goalId ? "goalComment" : "surveyComment"}
            surveyId={surveyId}
            goalId={goalId}
            onComplete={(result) => {
              // The survey coaches answer with a list, the goal coach with one comment.
              (Array.isArray(result) ? result : [result as Comment]).forEach(addComment);
              setFormData({ ...initialFormData, id: crypto.randomUUID() });
            }}
          />
        )}
      </div>
    </form>
  );
};

export default CommentForm;
